import React, { useState, useEffect } from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Text } from '@/components/common/text';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { useLazyListAllFeatureFlagsQuery } from '@/store/api/admin/featureFlagsApi';
import { FeatureFlagAttributes } from '@/types/admin/featureFlags';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useFeatureFlagActions } from '../hooks/useFeatureFlagActions';
import { FlagDetailsContent } from '../components/FlagDetailsContent';

interface DesktopFeatureFlagDetailsPanelProps {
  selectedFlag: FeatureFlagAttributes | null;
  isOrganizationView: boolean;
  onClose: () => void;
}

export const DesktopFeatureFlagDetailsPanel: React.FC<DesktopFeatureFlagDetailsPanelProps> = ({
  selectedFlag,
  isOrganizationView,
  onClose,
}) => {
  const { redesign } = useAdminColors();
  const [getFeatureFlags] = useLazyListAllFeatureFlagsQuery();
  const [localFlags, setLocalFlags] = useState(selectedFlag?.flags || {});

  const {
    editingKey,
    editValue,
    setEditValue,
    handleResetFlag,
    handleSubmitValue,
    startEditing,
    cancelEditing,
  } = useFeatureFlagActions({
    selectedFlag,
    isOrganizationView,
    localFlags,
    setLocalFlags,
  });

  useEffect(() => {
    setLocalFlags(selectedFlag?.flags || {});
  }, [selectedFlag]);

  const handleClose = () => {
    getFeatureFlags();
    onClose();
  };

  if (!selectedFlag) return null;

  const title = isOrganizationView
    ? (selectedFlag.organizationName || 'Organization')
    : (selectedFlag.propertyName || 'Property');

  return (
    <DesktopSidePanel visible={!!selectedFlag} onClose={handleClose} width={480}>
      <View style={styles.container}>
        <View style={[styles.header, { borderBottomColor: redesign.card.border }]}>
          <View style={styles.headerContent}>
            <Text style={[styles.title, { color: redesign.text.primary }]}>{title}</Text>
            {!isOrganizationView && selectedFlag.organizationName && (
              <Text style={[styles.subtitle, { color: redesign.text.secondary }]}>
                {selectedFlag.organizationName}
              </Text>
            )}
          </View>
          <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
            <SVG_COMPONENTS.X_CIRCLE
              width={24}
              height={24}
              color={redesign.text.primary}
            />
          </TouchableOpacity>
        </View>

        <View style={styles.content}>
          <FlagDetailsContent
            localFlags={localFlags}
            editingKey={editingKey}
            editValue={editValue}
            setEditValue={setEditValue}
            onSubmitValue={handleSubmitValue}
            onCancelEditing={cancelEditing}
            onStartEditing={startEditing}
            onResetFlag={handleResetFlag}
          />
        </View>
      </View>
    </DesktopSidePanel>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
  },
  subtitle: {
    fontSize: 14,
    marginTop: 4,
  },
  closeButton: {
    padding: 8,
  },
  content: {
    flex: 1,
    padding: 20,
  },
});
